import type { RecipeFilter } from '@shh/shared';
import { locale } from '../lib/locale';

/**
 * Rezepte (1.21, Küche 2): the collection, adding (link, screenshot, by hand), the recipe page,
 * the cooking view, "auf die Liste". Glossary: Rezept = recipe, Kochansicht = cooking view,
 * Portionen = servings, Kinder-Tipp = tip for kids. German is the master.
 */
export const RECIPES_TEXT = {
  de: {
    tabs: { shopping: 'Einkauf', recipes: 'Rezepte' },
    tabsLabel: 'Essen',
    search: 'Rezept oder Zutat suchen',
    filters: {
      quick: 'Schnell',
      veggie: 'Vegetarisch',
      kids: 'Kinder mögen’s',
      longAgo: 'Lange nicht',
    } satisfies Record<RecipeFilter, string>,
    filtersLabel: 'Filter',
    count: (n: number) => (n === 1 ? '1 Rezept' : `${n} Rezepte`),
    countFree: (n: number, max: number) => `${n} von ${max} Rezepten · kostenlos`,
    empty: 'Noch keine Rezepte. Fangt mit eurem Lieblingsessen an.',
    starters: 'Mit 3 Beispielrezepten starten',
    startersHint: 'Tomatenpasta, Ofengemüse, Hähnchen-Wraps – ändern oder löschen geht jederzeit.',
    noMatch: 'Kein Rezept passt dazu.',
    add: 'Rezept hinzufügen',
    minutes: (n: number) => `${n} Min.`,
    // adding
    addTitle: 'Rezept hinzufügen',
    fromPhoto: 'Screenshot oder Foto',
    fromPhotoHint:
      'Instagram, TikTok, Kochbuch, Zeitschrift. Larovi liest Zutaten und Schritte, du prüfst.',
    fromLink: 'Link einfügen',
    fromLinkHint: 'Chefkoch, Blogs, Küchenportale. Klappt meist sofort, ohne KI.',
    byHand: 'Selbst aufschreiben',
    byHandHint: 'Omas Rezept, eure eigene Variante.',
    shareTip: 'Tipp: Auf Android kannst du einen Screenshot direkt aus Instagram an Larovi teilen.',
    linkLabel: 'Link zum Rezept',
    linkExample: 'https://…',
    readLink: 'Rezept holen',
    reading: 'Larovi liest das Rezept …',
    aiCleanUp: 'Mit KI aufräumen',
    aiCleanUpHint:
      'Die Seite liefert kein lesbares Rezept. Mit Plus kann die KI es aus dem Text lesen.',
    photoLabel: 'Bild wählen',
    photoNote: 'Woher? (optional)',
    photoNoteExample: 'z. B. Unser Lieblingsbuch, S. 82',
    readPhoto: 'Rezept lesen',
    aiOff: 'Dafür muss der KI-Helfer an sein. Eltern schalten ihn in den Einstellungen ein.',
    toAiSettings: 'Zu den Einstellungen',
    // review / edit
    checkTitle: 'Rezept prüfen',
    editTitle: 'Rezept ändern',
    newTitle: 'Neues Rezept',
    checkHint: 'Bitte kurz prüfen – erst mit „Speichern“ steht es in euren Rezepten.',
    unsure: 'Bitte prüfen',
    title: 'Titel',
    servings: 'Portionen',
    totalMinutes: 'Zeit in Minuten (optional)',
    ingredients: 'Zutaten',
    ingredientsHint: 'Eine Zutat pro Zeile, Menge vorne: „400 g Spaghetti“.',
    steps: 'Schritte',
    stepsHint: 'Ein Schritt pro Zeile. Zeiten wie „10 Minuten“ werden zu Timern.',
    tags: 'Merkmale',
    kidsTip: 'Kinder-Tipp (optional)',
    kidsTipExample: 'z. B. Chili erst am Tisch',
    sourceNote: 'Quelle (optional)',
    sourceNoteExample: 'z. B. Omas Rezept',
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Rezept löschen',
    deleteConfirm: (title: string) => `„${title}“ wirklich löschen?`,
    deleteYes: 'Löschen',
    // recipe page
    back: 'Zurück zu den Rezepten',
    change: 'Rezept ändern',
    fewer: 'Weniger Portionen',
    more: 'Mehr Portionen',
    quickServings: 'Portionen schnell wählen',
    forServings: (n: number) => `Für ${n}`,
    toList: 'Auf die Einkaufsliste',
    kidsTipLabel: 'Kinder-Tipp',
    source: {
      own: 'Eigenes Rezept',
      link: (site: string) => `Von ${site}`,
      photo: 'Aus einem Screenshot oder Foto',
    },
    original: 'Original öffnen',
    lastCooked: (days: number) =>
      days === 0 ? 'heute gekocht' : days === 1 ? 'gestern gekocht' : `vor ${days} Tagen gekocht`,
    neverCooked: 'noch nie gekocht',
    cook: 'Kochen starten',
    notFound: 'Dieses Rezept gibt es nicht mehr.',
    // to the list
    toListTitle: 'Auf die Einkaufsliste',
    toListHint: (title: string, servings: number) =>
      `${title} · ${servings} Portionen. Was ihr schon habt, einfach abwählen.`,
    onListAlready: 'steht schon drauf',
    toListButton: (n: number) => (n === 1 ? '1 Sache auf die Liste' : `${n} Sachen auf die Liste`),
    added: (n: number) =>
      n === 1 ? '1 Sache steht auf der Liste.' : `${n} Sachen stehen auf der Liste.`,
    // cooking view
    step: (n: number, of: number) => `Schritt ${n} von ${of}`,
    close: 'Schließen',
    prev: 'Zurück',
    next: 'Weiter',
    done: 'Fertig gekocht',
    needed: 'Dafür brauchst du',
    timer: (min: number) => `Timer ${min} Min. starten`,
    timerRunning: 'Timer läuft',
    timerDone: 'Zeit ist um!',
    stopTimer: 'Timer beenden',
    awake: 'Der Bildschirm bleibt an, solange das Rezept offen ist.',
    textSize: 'Schriftgröße',
    smaller: 'Schrift kleiner',
    larger: 'Schrift größer',
    enjoy: 'Guten Appetit!',
    noSteps: 'Dieses Rezept hat noch keine Schritte.',
    addPhoto: 'Eigenes Foto',
    changePhoto: 'Foto ändern',
    removePhoto: 'Foto entfernen',
    photoAlt: (title: string) => `Foto: ${title}`,
  },
  en: {
    tabs: { shopping: 'Shopping', recipes: 'Recipes' },
    tabsLabel: 'Food',
    search: 'Search recipe or ingredient',
    filters: {
      quick: 'Quick',
      veggie: 'Vegetarian',
      kids: 'Kids like it',
      longAgo: 'Not for a while',
    } satisfies Record<RecipeFilter, string>,
    filtersLabel: 'Filters',
    count: (n: number) => (n === 1 ? '1 recipe' : `${n} recipes`),
    countFree: (n: number, max: number) => `${n} of ${max} recipes · free`,
    empty: 'No recipes yet. Start with your favourite meal.',
    starters: 'Start with 3 example recipes',
    startersHint: 'Tomato pasta, roast vegetables, chicken wraps – change or delete them any time.',
    noMatch: 'No recipe matches.',
    add: 'Add recipe',
    minutes: (n: number) => `${n} min`,
    addTitle: 'Add recipe',
    fromPhoto: 'Screenshot or photo',
    fromPhotoHint:
      'Instagram, TikTok, cookbook, magazine. Larovi reads the ingredients and steps, you check them.',
    fromLink: 'Paste a link',
    fromLinkHint: 'Recipe sites and blogs. Usually works straight away, without AI.',
    byHand: 'Write it down',
    byHandHint: 'Grandma’s recipe, your own version.',
    shareTip: 'Tip: on Android you can share a screenshot straight from Instagram to Larovi.',
    linkLabel: 'Link to the recipe',
    linkExample: 'https://…',
    readLink: 'Get recipe',
    reading: 'Larovi is reading the recipe …',
    aiCleanUp: 'Tidy up with AI',
    aiCleanUpHint: 'This page has no readable recipe. With Plus, the AI can read it from the text.',
    photoLabel: 'Choose a picture',
    photoNote: 'Where from? (optional)',
    photoNoteExample: 'e.g. Our favourite book, p. 82',
    readPhoto: 'Read recipe',
    aiOff: 'The AI helper needs to be on for this. Parents switch it on in the settings.',
    toAiSettings: 'Go to settings',
    checkTitle: 'Check the recipe',
    editTitle: 'Change recipe',
    newTitle: 'New recipe',
    checkHint: 'Please have a quick look – it is only in your recipes once you tap “Save”.',
    unsure: 'Please check',
    title: 'Title',
    servings: 'Servings',
    totalMinutes: 'Time in minutes (optional)',
    ingredients: 'Ingredients',
    ingredientsHint: 'One ingredient per line, amount first: “400 g spaghetti”.',
    steps: 'Steps',
    stepsHint: 'One step per line. Times like “10 minutes” become timers.',
    tags: 'Tags',
    kidsTip: 'Tip for kids (optional)',
    kidsTipExample: 'e.g. add the chilli at the table',
    sourceNote: 'Source (optional)',
    sourceNoteExample: 'e.g. Grandma’s recipe',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete recipe',
    deleteConfirm: (title: string) => `Really delete “${title}”?`,
    deleteYes: 'Delete',
    back: 'Back to recipes',
    change: 'Change recipe',
    fewer: 'Fewer servings',
    more: 'More servings',
    quickServings: 'Choose servings',
    forServings: (n: number) => `For ${n}`,
    toList: 'Add to the shopping list',
    kidsTipLabel: 'Tip for kids',
    source: {
      own: 'Our own recipe',
      link: (site: string) => `From ${site}`,
      photo: 'From a screenshot or photo',
    },
    original: 'Open the original',
    lastCooked: (days: number) =>
      days === 0 ? 'cooked today' : days === 1 ? 'cooked yesterday' : `cooked ${days} days ago`,
    neverCooked: 'never cooked yet',
    cook: 'Start cooking',
    notFound: 'This recipe no longer exists.',
    toListTitle: 'Add to the shopping list',
    toListHint: (title: string, servings: number) =>
      `${title} · ${servings} servings. Untick what you already have.`,
    onListAlready: 'already on the list',
    toListButton: (n: number) =>
      n === 1 ? 'Add 1 thing to the list' : `Add ${n} things to the list`,
    added: (n: number) => (n === 1 ? '1 thing is on the list.' : `${n} things are on the list.`),
    step: (n: number, of: number) => `Step ${n} of ${of}`,
    close: 'Close',
    prev: 'Back',
    next: 'Next',
    done: 'Finished cooking',
    needed: 'You need',
    timer: (min: number) => `Start ${min} min timer`,
    timerRunning: 'Timer running',
    timerDone: 'Time’s up!',
    stopTimer: 'Stop timer',
    awake: 'The screen stays on while the recipe is open.',
    textSize: 'Text size',
    smaller: 'Smaller text',
    larger: 'Larger text',
    enjoy: 'Enjoy your meal!',
    noSteps: 'This recipe has no steps yet.',
    addPhoto: 'Own photo',
    changePhoto: 'Change photo',
    removePhoto: 'Remove photo',
    photoAlt: (title: string) => `Photo: ${title}`,
  },
};

export const recipesText = RECIPES_TEXT[locale];
